import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  addAdditionalColleagues,
  getTeacherAccountLookup,
  resendTeacherSetupLink
} from "../services/TeacherAPIClient";
import { isPlaceholderWorkflowId } from "../utils/teacherWorkflowRequests";
import "./TeacherSendAccountSetupModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  email: String(prefill?.email || prefill?.teacherEmail || prefill?.teacher_email || "").trim()
});

const getTeacherStatus = (teacher) =>
  teacher?.status ||
  teacher?.accountStatus ||
  teacher?.account_status ||
  teacher?.inviteStatus ||
  teacher?.invite_status ||
  "Invited";

const normaliseTeacher = (teacher) => ({
  ...teacher,
  id: teacher?.pendingteacherid || teacher?.pendingTeacherId || teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId,
  pendingteacherid: teacher?.pendingteacherid || teacher?.pendingTeacherId || teacher?.pending_teacher_id || teacher?.id,
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
  email: teacher?.email || "",
  status: getTeacherStatus(teacher),
  availableActions: teacher?.availableActions || teacher?.available_actions || [],
  lastInviteSent: teacher?.lastinvitesent || teacher?.lastInviteSent || teacher?.last_invite_sent || ""
});

const getLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.teachers)) return lookupResponse.teachers;
  if (Array.isArray(lookupResponse?.rows)) return lookupResponse.rows;
  if (Array.isArray(lookupResponse?.accounts)) return lookupResponse.accounts;
  return [];
};

const findSingleExactTeacher = (teachers, prefillContext) => {
  const teacherName = normaliseText(prefillContext.teacherName);
  const email = normaliseText(prefillContext.email);

  if (!teacherName && !email) {
    return null;
  }

  const matches = teachers.filter((teacher) => {
    const nameMatches = teacherName && normaliseText(teacher.name) === teacherName;
    const emailMatches = email && normaliseText(teacher.email) === email;

    return nameMatches || emailMatches;
  });

  return matches.length === 1 ? matches[0] : null;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const splitTeacherName = (teacherName) => {
  const nameParts = teacherName.trim().replace(/\s+/g, " ").split(" ");
  const firstName = nameParts.shift() || "";
  const lastName = nameParts.join(" ") || firstName;

  return { firstName, lastName };
};

const getInviteFallbackFields = (prefillContext, searchTerm) => {
  const fallbackSearchTerm = searchTerm.trim();
  const fallbackIsEmail = isValidEmail(fallbackSearchTerm);

  return {
    name: prefillContext.teacherName || (fallbackIsEmail ? "" : fallbackSearchTerm),
    email: prefillContext.email || (fallbackIsEmail ? fallbackSearchTerm : "")
  };
};

function TeacherSendAccountSetupModal({
  show,
  handleClose,
  schoolid,
  rollnumber,
  rollNumber,
  teacherName,
  currentTeacherName,
  countryId,
  prefill = null,
  onSendSetupLink = null,
  onSendInvite = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("search");
  const [teachers, setTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [sentTeacher, setSentTeacher] = useState(null);
  const [sentAction, setSentAction] = useState("setupLink");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const trimmedSearchTerm = searchTerm.trim();
  const trimmedInviteName = inviteName.trim().replace(/\s+/g, " ");
  const trimmedInviteEmail = inviteEmail.trim();
  const canSendSetupLink = Boolean(selectedTeacher && !isSubmitting);
  const canSendInvite = Boolean(
    trimmedInviteName &&
    isValidEmail(trimmedInviteEmail) &&
    !isSubmitting
  );

  const filteredTeachers = useMemo(() => {
    const query = normaliseText(searchTerm);

    if (!query) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.status].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [searchTerm, teachers]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("search");
    setTeachers([]);
    setSearchTerm(prefillContext.teacherName || prefillContext.email);
    setSelectedTeacher(null);
    setInviteName(prefillContext.teacherName);
    setInviteEmail(prefillContext.email);
    setSentTeacher(null);
    setSentAction("setupLink");
    setError("");

    if (!schoolid) {
      setError("Invited teachers could not be loaded. Please try again.");
      return;
    }

    setIsLoading(true);
    getTeacherAccountLookup(schoolid)
      .then((teachersResponse) => {
        const normalisedTeachers = getLookupRows(teachersResponse)
          .map(normaliseTeacher)
          .filter((teacher) =>
            teacher.id &&
            (teacher.availableActions.includes("resend_setup_link") ||
              ["setup_not_completed", "invited", "pending_teacher", "Setup not completed", "Invited"].includes(teacher.status))
          )
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));
        const prefilledTeacher = findSingleExactTeacher(normalisedTeachers, prefillContext);

        setTeachers(normalisedTeachers);
        setSelectedTeacher(prefilledTeacher);
      })
      .catch((loadError) => {
        console.error("Error loading invited teachers:", loadError);
        setError("Invited teachers could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, schoolid, prefillContext]);

  const handleSelectTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    setError("");
  };

  const handleShowInviteFallback = () => {
    const fallbackFields = getInviteFallbackFields(prefillContext, searchTerm);

    setStage("invite");
    setInviteName(fallbackFields.name);
    setInviteEmail(fallbackFields.email);
    setSelectedTeacher(null);
    setError("");
  };

  const sendSetupLink = async () => {
    if (!canSendSetupLink) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (onSendSetupLink) {
        await onSendSetupLink(selectedTeacher);
      } else {
        const pendingTeacherId = selectedTeacher.pendingteacherid || selectedTeacher.id;

        if (isPlaceholderWorkflowId(pendingTeacherId)) {
          throw new Error("Missing real pending teacher id.");
        }

        await resendTeacherSetupLink(schoolid, pendingTeacherId);
      }

      setSentTeacher(selectedTeacher);
      setSentAction("setupLink");
      setStage("success");
    } catch (submitError) {
      console.error("Error sending account setup link:", submitError);
      setError("The setup link could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendInvite = async () => {
    if (!canSendInvite) {
      return;
    }

    const nameParts = splitTeacherName(trimmedInviteName);
    const newTeacher = {
      ...nameParts,
      name: trimmedInviteName,
      email: trimmedInviteEmail,
      roles: "Class teacher"
    };
    setIsSubmitting(true);
    setError("");

    try {
      if (onSendInvite) {
        await onSendInvite({ schoolid, teacher: newTeacher });
      } else if (schoolid) {
        await addAdditionalColleagues(
          schoolid,
          rollnumber || rollNumber,
          [newTeacher],
          currentTeacherName || teacherName,
          countryId
        );
      } else {
        throw new Error("Missing school context for teacher invite.");
      }

      setSentTeacher({
        ...newTeacher,
        id: trimmedInviteEmail,
        status: "Invited"
      });
      setSentAction("invite");
      setStage("success");
    } catch (submitError) {
      console.error("Error sending teacher invite:", submitError);
      setError("The invite could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherTeacher = () => {
    setStage("search");
    setSearchTerm("");
    setSelectedTeacher(null);
    setInviteName("");
    setInviteEmail("");
    setSentTeacher(null);
    setSentAction("setupLink");
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        message: sentAction === "invite" ? "Teacher invite sent." : "Account setup link sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="send-account-setup-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading invited teachers...</span>
        </div>
      );
    }

    if (!trimmedSearchTerm) {
      return (
        <p className="send-account-setup-muted">
          Search invited teachers by name or email.
        </p>
      );
    }

    if (filteredTeachers.length < 1) {
      return (
        <div className="send-account-setup-empty">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>Teacher not found</strong>
            <span>Invite the teacher first, then they can use the setup link.</span>
            <Button variant="outline-primary" size="sm" onClick={handleShowInviteFallback}>
              Invite teacher first
            </Button>
          </span>
        </div>
      );
    }

    return filteredTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`send-account-setup-result${selected ? " selected" : ""}`}
          onClick={() => handleSelectTeacher(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <span className="send-account-setup-radio" aria-hidden="true" />
          <span className="send-account-setup-result-copy">
            <strong>{teacher.name}</strong>
            <span>{teacher.email || "No email shown"}</span>
            <span className="send-account-setup-status">{teacher.status}</span>
          </span>
          {selected && <span className="send-account-setup-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderSelectedSummary = () => {
    if (!selectedTeacher) {
      return null;
    }

    return (
      <div className="send-account-setup-summary" aria-label="Selected teacher">
        <strong>Setup link destination</strong>
        <span className="send-account-setup-row">
          <span>Teacher</span>
          <span>{selectedTeacher.name}</span>
        </span>
        <span className="send-account-setup-row">
          <span>Email</span>
          <span>{selectedTeacher.email || "No email shown"}</span>
        </span>
        <span className="send-account-setup-row">
          <span>Status</span>
          <span>{selectedTeacher.status}</span>
        </span>
      </div>
    );
  };

  const renderSearch = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Resend Account Setup Link</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="send-account-setup-intro">Find the invited teacher you’d like to resend a setup link to.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group controlId="teacherSendAccountSetupSearch">
          <Form.Label>Which teacher needs a setup link resent?</Form.Label>
          <Form.Text as="div" className="send-account-setup-field-label">
            Search invited teacher
          </Form.Text>
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setSelectedTeacher(null);
              setError("");
            }}
            placeholder="Teacher name or email"
            aria-label="Search invited teacher"
          />
        </Form.Group>

        <div className="send-account-setup-results" aria-label="Matching invited teachers">
          {renderTeacherResults()}
        </div>
        {renderSelectedSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="send-account-setup-footer-note">
          {selectedTeacher ? `${selectedTeacher.name} selected.` : "Choose an invited teacher first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canSendSetupLink} onClick={sendSetupLink}>
          {isSubmitting ? "Sending..." : "Resend setup link"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderInviteFallback = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Invite Teacher First</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="send-account-setup-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>Teacher not found</strong>
            <span>Send an invite first. The teacher can set up their account from that email.</span>
          </span>
        </Alert>

        <Form.Group controlId="teacherSendAccountSetupInviteName">
          <Form.Label>Teacher name</Form.Label>
          <Form.Control
            type="text"
            value={inviteName}
            onChange={(event) => {
              setInviteName(event.target.value);
              setError("");
            }}
            aria-label="Teacher name"
          />
        </Form.Group>

        <Form.Group className="send-account-setup-section" controlId="teacherSendAccountSetupInviteEmail">
          <Form.Label>Teacher email</Form.Label>
          <Form.Control
            type="email"
            value={inviteEmail}
            onChange={(event) => {
              setInviteEmail(event.target.value);
              setError("");
            }}
            aria-label="Teacher email"
            isInvalid={Boolean(trimmedInviteEmail) && !isValidEmail(trimmedInviteEmail)}
          />
          <Form.Control.Feedback type="invalid">
            Enter a valid email address.
          </Form.Control.Feedback>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="send-account-setup-footer-note">
          {canSendInvite ? "Ready to send invite." : "Enter a teacher name and email."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("search")}>
          Back
        </Button>
        <Button variant="primary" disabled={!canSendInvite} onClick={sendInvite}>
          {isSubmitting ? "Sending..." : "Send invite"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">
          {sentAction === "invite" ? "Invite Sent" : "Setup Link Sent"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="success" className="send-account-setup-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              {sentAction === "invite"
                ? `An invite has been sent to ${sentTeacher?.name}.`
                : `The account setup link has been sent to ${sentTeacher?.name}.`}
            </strong>
            <span>{sentTeacher?.email}</span>
          </span>
        </Alert>
        <div className="send-account-setup-summary">
          <strong>What happens next</strong>
          <span>
            {sentAction === "invite"
              ? "They can use the invite email to set up their account."
              : "They can use the setup link from their email to finish creating their account."}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="send-account-setup-footer-note">
          {embedded ? "Done returns to SENSAI." : "Sent."}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnotherTeacher}>
          Send another setup link
        </Button>
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-send-account-setup-modal"
    >
      {stage === "search" && renderSearch()}
      {stage === "invite" && renderInviteFallback()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherSendAccountSetupModal;
